import { Link, NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import PageErrorBoundary from "@/components/PageErrorBoundary";
import { useAuth } from "@/context/AuthContext";
import {
    LayoutDashboard, Users, Briefcase, Gavel, CheckSquare, FileText,
    Receipt, MessageSquare, MessageCircle, Workflow, Sparkles, Settings,
    LogOut, Building2, CreditCard, BarChart3, ChevronDown, Plug, Menu, X,
    UserCog, TrendingUp, ShieldAlert, ShieldCheck, ToggleRight, Search,
    Calendar as CalendarIcon, Video, Database, FileSignature, Gift,
    Palette, Brain, Wallet, Shield, Scale, Bell, BookOpen, Edit3, Hash, Quote,
    KeyRound, LifeBuoy, Plus, Activity, AlertTriangle, BellRing, HardDrive, GitBranch, Zap, Mail as MailIcon
} from "lucide-react";
import { useBranding } from "@/context/BrandingContext";
import PushPermissionBanner from "@/components/PushPermissionBanner";
import PushInstallModal from "@/components/PushInstallModal";
import IosInstallNudge from "@/components/IosInstallNudge";
import ChatWidget from "@/components/ChatWidget";
import CommandPalette from "@/components/CommandPalette";
import { useState, useEffect, useCallback, useRef } from "react";
import NotificationBell from "@/components/NotificationBell";
import ThemeToggle from "@/components/ThemeToggle";
import BillingBanner from "@/components/BillingBanner";
import OnboardingGate from "@/components/OnboardingGate";
import FounderBadge from "@/components/FounderBadge";
import { useFeatures } from "@/context/FeaturesContext";
import { useIdleLogout } from "@/lib/useIdleLogout";
import { api } from "@/lib/api";

// ─────────── Law OS v2 — Top-bar IA (hover-dropdown menus per category) ──────────
// Layout is now horizontal: logo · category menus · global search · utilities · user.
// Mobile collapses to a hamburger drawer with the same categorized accordion.

const TENANT_MENUS = [
    {
        key: "dashboard",
        label: "Dashboard",
        to: "/app",
        end: true,
        testId: "nav-overview",
        icon: LayoutDashboard,
    },
    {
        key: "work",
        label: "Work",
        icon: Briefcase,
        items: [
            { to: "/app/my-week",     label: "My Week",      icon: CalendarIcon,  testId: "nav-my-week",      flag: "module_my_week" },
            { to: "/app/cases",       label: "Litigations",        icon: Briefcase,     testId: "nav-cases",        flag: "module_cases" },
            { to: "/app/hearings",    label: "Hearings",     icon: Gavel,         testId: "nav-hearings",     flag: "module_hearings" },
            { to: "/app/cause-list",  label: "Cause List",   icon: Gavel,         testId: "nav-cause-list",   flag: "module_hearings" },
            { to: "/app/tasks",       label: "Tasks",        icon: CheckSquare,   testId: "nav-tasks",        flag: "module_tasks" },
            { to: "/app/calendar",    label: "Calendar",     icon: CalendarIcon,  testId: "nav-calendar",     flag: "module_calendar" },
            { to: "/app/automations", label: "Automations",  icon: Workflow,      testId: "nav-automations",  flag: "automations" },
        ],
    },
    {
        key: "clients",
        label: "Clients",
        icon: Users,
        items: [
            { to: "/app/clients",        label: "Clients",        icon: Users,         testId: "nav-clients",        flag: "module_clients" },
            { to: "/app/communications", label: "Communications", icon: MessageSquare, testId: "nav-communications", flag: ["communications_email", "communications_whatsapp"] },
            { to: "/app/chat",           label: "Client Chat",    icon: MessageSquare, testId: "nav-chat",           flag: "module_chat" },
        ],
    },
    {
        key: "documents",
        label: "Documents",
        icon: FileText,
        items: [
            { to: "/app/documents", label: "Documents",    icon: FileText,      testId: "nav-documents", flag: "module_documents" },
            { to: "/app/legal-search", label: "Legal Search", icon: Search,    testId: "nav-legal-search", flag: "legal_search" },
            { to: "/app/ai-docs",   label: "AI Drafts",    icon: FileSignature, testId: "nav-ai-docs",   flag: "ai_docs" },
            { to: "/app/ai",        label: "AI Assistant", icon: Sparkles,      testId: "nav-ai-assistant", flag: "ai_assistant" },
        ],
    },
    {
        key: "operations",
        label: "Operations",
        icon: BarChart3,
        items: [
            { to: "/app/staff",          label: "Team",         icon: UserCog,    testId: "nav-staff",        flag: "module_staff" },
            { to: "/app/invoices",       label: "Billing",      icon: Receipt,    testId: "nav-invoices",     flag: "module_invoices" },
            { to: "/app/accounting",     label: "Accounting",   icon: BookOpen,   testId: "nav-accounting",   flag: "accounting", roleAllowed: ["firm_owner", "managing_partner", "accountant", "senior_advocate"] },
            { to: "/app/accounting?legal=1", label: "Quick Legal Entry", icon: Briefcase, testId: "nav-legal-entry", flag: "accounting", roleAllowed: ["firm_owner", "managing_partner", "accountant", "senior_advocate"] },
            { to: "/app/analytics",      label: "Reports",      icon: BarChart3,  testId: "nav-analytics",    roleAllowed: ["firm_owner", "managing_partner"], flag: "analytics" },
            { to: "/app/productivity",   label: "Productivity", icon: TrendingUp, testId: "nav-productivity", flag: "module_productivity" },
            { to: "/app/ecourts",        label: "eCourts Sync", icon: Scale,      testId: "nav-ecourts",      roleAllowed: ["firm_owner", "managing_partner"], flag: "ecourts_sync" },
            { to: "/app/data-integrity", label: "Data Integrity", icon: ShieldCheck, testId: "nav-data-integrity", roleAllowed: ["firm_owner", "managing_partner"] },
            { to: "/app/court-vc-links", label: "Court VC links", icon: Video,    testId: "nav-vc-links",     roleAllowed: ["firm_owner", "managing_partner"], flag: "court_vc_links" },
            { to: "/app/wallet",         label: "AI Wallet",    icon: Wallet,     testId: "nav-wallet",       flag: "ai_wallet" },
            { to: "/app/referrals",      label: "Referrals",    icon: Gift,       testId: "nav-referrals",    flag: "module_referrals" },
        ],
    },
    {
        key: "settings",
        label: "Settings",
        icon: Settings,
        items: [
            { to: "/app/access-control",   label: "Access Control",   icon: Shield,      testId: "nav-access-control", roleAllowed: ["firm_owner", "managing_partner"] },
            { to: "/app/audit",            label: "Audit log",        icon: ShieldAlert, testId: "nav-audit",          roleAllowed: ["firm_owner", "managing_partner"], flag: "audit_log" },
            { to: "/app/compliance",       label: "Compliance",       icon: ShieldCheck, testId: "nav-compliance",     roleAllowed: ["firm_owner", "managing_partner"], flag: "compliance_posture" },
            { to: "/app/field-masks",      label: "Field Masks",      icon: ShieldAlert, testId: "nav-field-masks",    roleAllowed: ["firm_owner", "managing_partner"], flag: "advanced_rbac" },
            { to: "/app/api-keys",         label: "API Keys",         icon: KeyRound,    testId: "nav-api-keys",       roleAllowed: ["firm_owner", "managing_partner"], flag: "api_access" },
            { to: "/app/white-label",      label: "White-label",      icon: Palette,     testId: "nav-white-label",    roleAllowed: ["firm_owner", "managing_partner"], flag: "white_label" },
            { to: "/app/priority-support", label: "Priority Support", icon: LifeBuoy,    testId: "nav-priority-support", flag: "priority_support" },
            { to: "/app/notifications",    label: "Notifications",    icon: Bell,        testId: "nav-notifications" },
            { to: "/app/push-settings",    label: "Mobile Push",      icon: Bell,        testId: "nav-push-settings",  roleAllowed: ["firm_owner", "managing_partner"] },
            { to: "/app/docs",             label: "Documentation",    icon: BookOpen,    testId: "nav-docs" },
            { to: "/app/settings",         label: "Settings",         icon: Settings,    testId: "nav-settings" },
            { to: "/app/sign-in-activity", label: "Sign-in Activity", icon: Shield,      testId: "nav-signin-activity" },
            { to: "/app/settings/invoice-template", label: "Invoice Template", icon: FileText, testId: "nav-tenant-invoice-template", roleAllowed: ["firm_owner", "managing_partner", "firm_admin"] },
            { to: "/app/billing/plans",    label: "Plans & Upgrade",  icon: CreditCard,  testId: "nav-billing-plans",  roleAllowed: ["firm_owner", "managing_partner"] },
            { to: "/app/settings/billing/invoices", label: "Subscription Invoices", icon: Receipt, testId: "nav-tenant-subscription-invoices", roleAllowed: ["firm_owner", "managing_partner", "firm_admin"] },
            { to: "/app/billing/addons",   label: "Add-ons",          icon: Plus,        testId: "nav-billing-addons", roleAllowed: ["firm_owner", "managing_partner"] },
        ],
    },
];

const ADMIN_MENUS = [
    { key: "admin-overview", label: "Overview", to: "/admin", end: true, icon: BarChart3, testId: "nav-admin-overview" },
    { key: "admin-sm", label: "Sales & Marketing", to: "/sm", icon: TrendingUp, testId: "nav-admin-sm", external: true },
    {
        key: "admin-tenants", label: "Tenants", icon: Building2,
        items: [
            { to: "/admin/tenants",       label: "Tenants",          icon: Building2, testId: "nav-admin-tenants" },
            { to: "/admin/team",          label: "Platform Team",    icon: Users,     testId: "nav-admin-team" },
            { to: "/admin/subscriptions", label: "Subscriptions",    icon: Receipt,   testId: "nav-admin-subscriptions" },
            { to: "/admin/onboarding",    label: "Onboarding funnel", icon: TrendingUp, testId: "nav-admin-onboarding" },
        ],
    },
    {
        key: "admin-plans", label: "Plans & Billing", icon: CreditCard,
        items: [
            { to: "/admin/plans",              label: "Plans",                  icon: CreditCard, testId: "nav-admin-plans" },
            { to: "/admin/topup-packs",        label: "Top-up Packs",           icon: CreditCard, testId: "nav-admin-topup-packs" },
            { to: "/admin/pricing-cms",        label: "Pricing CMS",            icon: CreditCard, testId: "nav-admin-pricing-cms" },
            { to: "/admin/suggestion-rules",   label: "Upgrade Suggestions",    icon: Sparkles,   testId: "nav-admin-suggestion-rules" },
            { to: "/admin/invoice-template",   label: "Invoice Template",       icon: FileText,   testId: "nav-admin-invoice-template" },
            { to: "/admin/invoice-branding",   label: "Invoice Branding",       icon: FileText,   testId: "nav-admin-invoice-branding" },
            { to: "/admin/accounting",         label: "Accounting",             icon: BookOpen,   testId: "nav-admin-accounting" },
            { to: "/admin/tenant-accounting",  label: "Tenant books",           icon: BookOpen,   testId: "nav-admin-tenant-acc" },
            { to: "/admin/billing-automation", label: "Billing Automation",     icon: CreditCard, testId: "nav-admin-billing-auto" },
        ],
    },
    {
        key: "admin-platform", label: "Platform", icon: ToggleRight,
        items: [
            { to: "/admin/features",         label: "Feature flags",      icon: ToggleRight, testId: "nav-admin-features" },
            { to: "/admin/integrations",     label: "Integrations",       icon: Plug,        testId: "nav-admin-integrations" },
            { to: "/admin/ecourts",          label: "eCourts API",        icon: Scale,       testId: "nav-admin-ecourts" },
            { to: "/admin/scraper",          label: "Scraper API",        icon: Database,    testId: "nav-admin-scraper" },
            { to: "/admin/spend-analysis",   label: "Vendor spend",       icon: TrendingUp,  testId: "nav-admin-spend-analysis" },
            { to: "/admin/indiankanoon",     label: "Indian Kanoon",      icon: BookOpen,    testId: "nav-admin-indiankanoon" },
            { to: "/admin/master-data",      label: "Master data",        icon: Database,    testId: "nav-admin-master-data" },
            { to: "/admin/case-types",       label: "Litigation-type prefixes", icon: Hash,        testId: "nav-admin-case-types" },
            { to: "/admin/court-vc-links",   label: "Court VC links",     icon: Video,       testId: "nav-admin-vc-links" },
            { to: "/admin/branding",         label: "Branding",           icon: Palette,     testId: "nav-admin-branding" },
            { to: "/admin/voices",           label: "Voices CMS",         icon: Quote,       testId: "nav-admin-voices" },
            { to: "/admin/ai-governance",    label: "AI Governance",      icon: Brain,       testId: "nav-admin-ai-governance" },
            { to: "/admin/automations",      label: "Automations",         icon: Zap,         testId: "nav-admin-automations" },
        ],
    },
    {
        key: "admin-trust", label: "Trust & Security", icon: ShieldAlert,
        items: [
            { to: "/admin/audit",            label: "Audit log",           icon: ShieldAlert, testId: "nav-admin-audit" },
            { to: "/admin/security",         label: "Security",            icon: ShieldAlert, testId: "nav-admin-security" },
            { to: "/admin/sessions",         label: "Sessions & Auth",     icon: KeyRound,    testId: "nav-admin-sessions" },
            { to: "/admin/access-defaults",  label: "Access Defaults",     icon: Shield,      testId: "nav-admin-access-defaults" },
            { to: "/admin/enterprise",       label: "Enterprise Controls", icon: ShieldAlert, testId: "nav-admin-enterprise" },
            { to: "/admin/priority-support", label: "Priority Support",    icon: LifeBuoy,    testId: "nav-admin-priority-support" },
            { to: "/admin/legal",            label: "Legal & Compliance",  icon: FileText,    testId: "nav-admin-legal" },
        ],
    },
    {
        key: "admin-comms", label: "Comms & Docs", icon: MessageCircle,
        items: [
            { to: "/admin/notifications",  label: "Notifications",     icon: Bell,         testId: "nav-admin-notifications" },
            { to: "/admin/delivery-chains", label: "Delivery Chains",  icon: Zap,          testId: "nav-admin-delivery-chains" },
            { to: "/admin/comms",          label: "Delivery Report",    icon: MailIcon,     testId: "nav-admin-comms" },
            { to: "/admin/push-settings",  label: "Mobile Push",        icon: Bell,         testId: "nav-admin-push-settings" },
            { to: "/admin/system-health",  label: "System Health",      icon: Activity,     testId: "nav-admin-system-health" },
            { to: "/admin/data-integrity", label: "Data Integrity",     icon: ShieldCheck,  testId: "nav-admin-data-integrity" },
            { to: "/admin/broadcast",     label: "Broadcast Push",     icon: BellRing,     testId: "nav-admin-broadcast" },
            { to: "/admin/backups",        label: "Backups & DR",       icon: HardDrive,    testId: "nav-admin-backups" },
            { to: "/admin/system",         label: "System & Sync",      icon: GitBranch,    testId: "nav-admin-system" },
            { to: "/admin/chatbot",        label: "Support chats",     icon: MessageCircle, testId: "nav-admin-chatbot" },
            { to: "/admin/docs",           label: "Documentation",     icon: BookOpen,     testId: "nav-admin-docs" },
            { to: "/admin/documentation",  label: "Documentation CMS", icon: Edit3,        testId: "nav-admin-docs-cms" },
            { to: "/admin/referrals",      label: "Referrals",         icon: Gift,         testId: "nav-admin-referrals" },
        ],
    },
];

// Back-compat exports — pages still import TENANT_NAV.
export const TENANT_NAV = TENANT_MENUS.flatMap(m => m.items || [{ to: m.to, label: m.label, icon: m.icon, end: m.end, testId: m.testId }]);

function useRoleAndFlags() {
    const { user } = useAuth();
    const { flags } = useFeatures();
    const allow = useCallback((item) => {
        if (item.roleAllowed && !item.roleAllowed.includes(user?.role)) return false;
        if (!item.flag) return true;
        if (Array.isArray(item.flag)) return item.flag.some(k => flags[k] !== false);
        return flags[item.flag] !== false;
    }, [user, flags]);
    return { user, allow };
}

export default function DashboardLayout({ mode = "tenant" }) {
    const { user, logout } = useAuth();
    const { allow } = useRoleAndFlags();
    const navigate = useNavigate();
    const handleAutoLogout = useCallback(async () => {
        try { await logout(); } catch { /* ignore — we still want to navigate */ }
        navigate("/login");
    }, [logout, navigate]);
    useIdleLogout(handleAutoLogout);

    const rawMenus = mode === "admin" ? ADMIN_MENUS : TENANT_MENUS;
    const menus = rawMenus
        .map(m => m.items ? { ...m, items: m.items.filter(allow) } : m)
        .filter(m => !m.items || m.items.length > 0);

    const [drawerOpen, setDrawerOpen] = useState(false);
    const [userMenuOpen, setUserMenuOpen] = useState(false);
    const branding = useBranding();
    const [n1, ...rest] = (branding.app_name || "Law OS").split(" ");
    const n2 = rest.join(" ");
    // Resolve the display knobs. Clamp height to the supported range so a
    // bad DB value never breaks the topbar height (header is 64px tall).
    const logoH = Math.max(24, Math.min(56, Number(branding.logo_display_height) || 32));
    const layout = branding.logo_layout || "icon_and_text";
    const showIcon = layout !== "text_only";
    const showText = layout !== "icon_only";

    const closeDrawer = useCallback(() => setDrawerOpen(false), []);

    return (
        <div className="min-h-screen bg-background text-foreground">
            <IosInstallNudge/>
            {/* TOP BAR ──────────────────────────────────────────────────────────── */}
            <header className="fixed top-0 left-0 right-0 h-16 z-30 backdrop-blur-xl bg-card/90 border-b border-border" data-testid="topbar">
                <div className="h-full max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between gap-4">
                    {/* Logo */}
                    <Link to={mode === "admin" ? "/admin" : "/app"} className="flex items-center gap-2.5 shrink-0" data-testid="topbar-logo">
                        {showIcon && (branding.logo_src ? (
                            <img src={branding.logo_src} alt={branding.app_name}
                                style={{ height: logoH, width: "auto", maxWidth: logoH * 2.5 }}
                                className="object-contain"
                                onError={(e) => { e.target.style.display = "none"; }}/>
                        ) : (
                            <div style={{ height: logoH, width: logoH }}
                                className="rounded-lg bg-primary flex items-center justify-center shrink-0">
                                <span className="font-heading text-primary-foreground font-bold"
                                    style={{ fontSize: logoH * 0.45 }}>{(branding.app_name || "L")[0]}</span>
                            </div>
                        ))}
                        {showText && (
                            <div className={showIcon ? "hidden sm:block" : "block"}>
                                <div className="font-heading text-[15px] leading-none font-semibold tracking-tight">
                                    {n1}{n2 && <span className="text-primary"> {n2}</span>}
                                </div>
                                <div className="text-[9px] uppercase tracking-[0.22em] text-muted-foreground mt-0.5">
                                    {mode === "admin" ? "Platform" : "Workspace"}
                                </div>
                            </div>
                        )}
                    </Link>

                    {/* Desktop menu — hover dropdowns */}
                    <nav className="hidden lg:flex items-center gap-1 flex-1 justify-center" data-testid="topbar-nav">
                        {menus.map(m => (
                            m.items ? <MenuDropdown key={m.key} menu={m}/> : <MenuLink key={m.key} menu={m}/>
                        ))}
                    </nav>

                    {/* Right utilities */}
                    <div className="flex items-center gap-1 sm:gap-2 shrink-0">
                        {mode === "tenant" && user?.is_founder && <FounderBadge className="hidden md:inline-flex"/>}
                        {mode === "tenant" && <CommandPaletteHint/>}
                        {mode === "tenant" && <CommandPaletteMobileTrigger/>}
                        <NotificationBell/>
                        <ThemeToggle/>
                        <UserAvatarMenu
                            user={user}
                            open={userMenuOpen}
                            setOpen={setUserMenuOpen}
                            onProfile={() => navigate("/app/profile")}
                            onLogout={async () => { await logout(); navigate("/login"); }}
                        />
                        {/* Hamburger (mobile) */}
                        <button onClick={() => setDrawerOpen(true)} data-testid="open-mobile-menu" aria-label="Open menu"
                            className="lg:hidden p-2 -mr-1 rounded-lg hover:bg-muted text-foreground">
                            <Menu className="w-5 h-5" strokeWidth={1.5}/>
                        </button>
                    </div>
                </div>
            </header>

            {mode === "admin" && <DemoDataLeakBanner/>}

            {/* MOBILE DRAWER ─────────────────────────────────────────────────────── */}
            {drawerOpen && (
                <>
                    <div className="fixed inset-0 bg-background/40 z-40 lg:hidden" onClick={() => setDrawerOpen(false)} data-testid="mobile-drawer-backdrop"/>
                    <aside className="fixed top-0 right-0 h-screen w-[300px] bg-card border-l border-border z-50 lg:hidden flex flex-col" data-testid="mobile-drawer">
                        <div className="h-16 px-5 flex items-center justify-between border-b border-border">
                            <div className="font-heading text-sm font-semibold">Menu</div>
                            <button onClick={() => setDrawerOpen(false)} data-testid="close-mobile-menu" className="text-muted-foreground hover:text-foreground p-1">
                                <X className="w-5 h-5" strokeWidth={1.5}/>
                            </button>
                        </div>
                        <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-1">
                            {menus.map(m => (
                                m.items ? <MobileGroup key={m.key} menu={m} onNavigate={closeDrawer}/> : (
                                    <MobileLink key={m.key} item={{ to: m.to, label: m.label, icon: m.icon, end: m.end, testId: m.testId }} onNavigate={closeDrawer}/>
                                )
                            ))}
                        </nav>
                    </aside>
                </>
            )}

            {/* CONTENT ───────────────────────────────────────────────────────────── */}
            <main className="pt-16 min-h-screen min-w-0">
                <PushPermissionBanner/>
                {mode === "tenant" && <BillingBanner/>}
                <div className="p-4 sm:p-6 lg:p-8 max-w-[1600px] mx-auto min-w-0">
                    {mode === "tenant"
                        ? <OnboardingGate><PageErrorBoundary safeRoute="/app"><Outlet/></PageErrorBoundary></OnboardingGate>
                        : <PageErrorBoundary safeRoute="/admin"><Outlet/></PageErrorBoundary>}
                </div>
            </main>
            <ChatWidget/>
            <PushInstallModal/>
            <CommandPalette/>
        </div>
    );
}

// ─────────── Menu primitives ──────────────────────────────────────────────────

function MenuLink({ menu }) {
    return (
        <NavLink
            to={menu.to}
            end={menu.end}
            data-testid={menu.testId}
            className={({ isActive }) =>
                `inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    isActive
                        ? "bg-primary/10 text-primary"
                        : "text-foreground/80 hover:text-foreground hover:bg-muted"
                }`
            }>
            <menu.icon className="w-4 h-4" strokeWidth={1.75}/>
            <span>{menu.label}</span>
        </NavLink>
    );
}

/** Hover-dropdown menu — opens automatically on mouseenter (no click required),
 *  with a small grace timeout so the cursor can travel between trigger and panel. */
function MenuDropdown({ menu }) {
    const location = useLocation();
    const [open, setOpen] = useState(false);
    const timer = useRef(null);
    const wrap = useRef(null);

    const childActive = menu.items.some(it =>
        location.pathname === it.to || location.pathname.startsWith(it.to + "/"));

    const openNow = () => { if (timer.current) clearTimeout(timer.current); setOpen(true); };
    const closeSoon = () => { timer.current = setTimeout(() => setOpen(false), 140); };

    // Close on outside click (keyboard users / touch)
    useEffect(() => {
        if (!open) return;
        const onDoc = (e) => { if (wrap.current && !wrap.current.contains(e.target)) setOpen(false); };
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, [open]);

    return (
        <div ref={wrap} className="relative" onMouseEnter={openNow} onMouseLeave={closeSoon}>
            <button
                type="button"
                onFocus={openNow}
                onClick={() => setOpen(o => !o)}
                data-testid={`menu-trigger-${menu.key}`}
                aria-haspopup="menu"
                aria-expanded={open}
                className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    childActive || open
                        ? "bg-primary/10 text-primary"
                        : "text-foreground/80 hover:text-foreground hover:bg-muted"
                }`}>
                <menu.icon className="w-4 h-4" strokeWidth={1.75}/>
                <span>{menu.label}</span>
                <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} strokeWidth={2}/>
            </button>
            {open && (
                <div
                    role="menu"
                    data-testid={`menu-panel-${menu.key}`}
                    className="absolute top-full left-1/2 -translate-x-1/2 mt-1.5 w-64 bg-card border border-border rounded-xl shadow-lawos-lg p-1.5 z-40 animate-in fade-in slide-in-from-top-1 duration-150">
                    {menu.items.map(item => (
                        <NavLink
                            key={item.to}
                            to={item.to}
                            data-testid={item.testId}
                            onClick={() => setOpen(false)}
                            className={({ isActive }) =>
                                `flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors ${
                                    isActive
                                        ? "bg-primary/10 text-primary font-medium"
                                        : "text-foreground/85 hover:bg-muted"
                                }`
                            }>
                            <item.icon className="w-4 h-4 shrink-0 opacity-70" strokeWidth={1.75}/>
                            <span className="truncate">{item.label}</span>
                        </NavLink>
                    ))}
                </div>
            )}
        </div>
    );
}

function MobileGroup({ menu, onNavigate }) {
    const location = useLocation();
    const childActive = menu.items.some(it =>
        location.pathname === it.to || location.pathname.startsWith(it.to + "/"));
    const [open, setOpen] = useState(childActive);
    return (
        <div>
            <button
                type="button"
                onClick={() => setOpen(o => !o)}
                data-testid={`mobile-group-${menu.key}`}
                className="w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-sm font-medium text-foreground/85 hover:bg-muted">
                <span className="inline-flex items-center gap-2.5">
                    <menu.icon className="w-4 h-4 opacity-70" strokeWidth={1.75}/>
                    {menu.label}
                </span>
                <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} strokeWidth={2}/>
            </button>
            {open && (
                <div className="mt-0.5 ml-3 pl-3 border-l border-border space-y-0.5">
                    {menu.items.map(item => <MobileLink key={item.to} item={item} onNavigate={onNavigate}/>)}
                </div>
            )}
        </div>
    );
}

function MobileLink({ item, onNavigate }) {
    return (
        <NavLink
            to={item.to}
            end={item.end}
            data-testid={item.testId}
            onClick={onNavigate}
            className={({ isActive }) =>
                `flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors ${
                    isActive
                        ? "bg-primary/10 text-primary font-medium"
                        : "text-foreground/85 hover:bg-muted"
                }`
            }>
            <item.icon className="w-4 h-4 opacity-70 shrink-0" strokeWidth={1.75}/>
            <span className="truncate">{item.label}</span>
        </NavLink>
    );
}

function UserAvatarMenu({ user, open, setOpen, onProfile, onLogout }) {
    const ref = useRef(null);
    useEffect(() => {
        if (!open) return;
        const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, [open, setOpen]);
    return (
        <div ref={ref} className="relative">
            <button
                onClick={() => setOpen(o => !o)}
                data-testid="topbar-user-menu"
                aria-haspopup="menu"
                aria-expanded={open}
                className="flex items-center gap-2 pl-1 pr-2 py-1 rounded-lg hover:bg-muted transition-colors">
                {user?.photo_v ? (
                    <img
                        src={`${process.env.REACT_APP_BACKEND_URL || ""}/api/users/${user.id}/photo?v=${user.photo_v}`}
                        alt={user?.name}
                        className="w-8 h-8 rounded-full object-cover bg-primary"
                        onError={(e) => { e.target.style.display = "none"; }}
                        data-testid="topbar-user-avatar"
                    />
                ) : (
                    <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-medium text-sm" data-testid="topbar-user-initials">
                        {user?.name?.[0]?.toUpperCase() || "U"}
                    </div>
                )}
                <ChevronDown className="w-3.5 h-3.5 text-muted-foreground hidden sm:block" strokeWidth={2}/>
            </button>
            {open && (
                <div className="absolute right-0 top-full mt-2 w-60 bg-card border border-border rounded-xl shadow-lawos-lg p-1.5 z-40">
                    <div className="px-3 py-2 border-b border-border mb-1">
                        <div className="text-sm font-medium truncate">{user?.name || "—"}</div>
                        <div className="text-[11px] text-muted-foreground truncate">{user?.email}</div>
                        {user?.role && (
                            <div className="text-[10px] uppercase tracking-[0.18em] text-primary mt-1">{user.role.replace(/_/g, " ")}</div>
                        )}
                    </div>
                    <button
                        onClick={() => { setOpen(false); onProfile(); }}
                        data-testid="profile-link"
                        className="w-full text-left px-3 py-2 rounded-lg text-sm hover:bg-muted flex items-center gap-2">
                        <UserCog className="w-4 h-4 opacity-70" strokeWidth={1.75}/> My profile
                    </button>
                    <button
                        onClick={() => { setOpen(false); onLogout(); }}
                        data-testid="logout-button"
                        className="w-full text-left px-3 py-2 rounded-lg text-sm hover:bg-muted flex items-center gap-2 text-foreground">
                        <LogOut className="w-4 h-4 opacity-70" strokeWidth={1.75}/> Sign out
                    </button>
                </div>
            )}
        </div>
    );
}

function CommandPaletteHint() {
    const isMac = typeof navigator !== "undefined" && /Mac|iPod|iPhone|iPad/.test(navigator.platform);
    const open = () => {
        // Dispatch synthetic Cmd/Ctrl+K so the global listener in CommandPalette toggles
        const evt = new KeyboardEvent("keydown", { key: "k", metaKey: isMac, ctrlKey: !isMac, bubbles: true });
        window.dispatchEvent(evt);
    };
    return (
        <button onClick={open} data-testid="topbar-cmdk-hint"
            className="hidden md:inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-border bg-muted/40 hover:border-primary/40 hover:bg-muted text-xs text-muted-foreground transition-colors">
            <Search className="w-3.5 h-3.5" strokeWidth={1.75}/>
            <span>Search…</span>
            <kbd className="ml-2 inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] uppercase tracking-wider bg-card border border-border rounded text-foreground/70">
                {isMac ? "⌘" : "Ctrl"} K
            </kbd>
        </button>
    );
}

/** Mobile-only icon button that opens the same ⌘K Command Palette. */
function CommandPaletteMobileTrigger() {
    const open = () => {
        const evt = new KeyboardEvent("keydown", { key: "k", ctrlKey: true, bubbles: true });
        window.dispatchEvent(evt);
    };
    return (
        <button onClick={open} data-testid="topbar-cmdk-mobile" aria-label="Open search"
            className="md:hidden p-2 rounded-lg hover:bg-muted text-foreground/80 hover:text-primary transition-colors">
            <Search className="w-5 h-5" strokeWidth={1.5}/>
        </button>
    );
}

function CourtCaseSearch({ onPick }) {
    const [q, setQ] = useState("");
    const [results, setResults] = useState([]);
    const [open, setOpen] = useState(false);
    const runSearch = useCallback(async (term) => {
        if (!term || term.trim().length < 2) { setResults([]); return; }
        try {
            const { data } = await api.get(`/search/court-case`, { params: { q: term.trim() } });
            setResults(data);
            setOpen(true);
        } catch { setResults([]); }
    }, []);
    useEffect(() => {
        const t = setTimeout(() => { runSearch(q); }, 250);
        return () => clearTimeout(t);
    }, [q, runSearch]);
    return (
        <div className="relative hidden md:block">
            <div className="relative">
                <Search className="w-3.5 h-3.5 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" strokeWidth={1.75}/>
                <input
                    value={q}
                    onChange={e => setQ(e.target.value)}
                    onFocus={() => results.length > 0 && setOpen(true)}
                    onBlur={() => setTimeout(() => setOpen(false), 150)}
                    placeholder="Search litigations…"
                    data-testid="topbar-case-search"
                    className="w-48 lg:w-60 bg-muted/40 border border-border focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none pl-8 pr-3 py-2 text-xs rounded-lg placeholder:text-muted-foreground"
                />
            </div>
            {open && results.length > 0 && (
                <div className="absolute right-0 mt-1 w-[420px] bg-card border border-border rounded-xl shadow-lawos-lg z-40 max-h-[60vh] overflow-y-auto" data-testid="topbar-case-search-results">
                    {results.map(r => (
                        <button key={r.id} onMouseDown={() => { onPick(r.id); setQ(""); setOpen(false); }}
                            className="w-full text-left px-3 py-2 border-b border-border last:border-0 hover:bg-muted"
                            data-testid={`search-result-${r.id}`}>
                            <div className="flex items-center justify-between gap-2">
                                <div className="text-sm truncate">{r.title || "(untitled)"}</div>
                                <span className="text-[9px] uppercase tracking-[0.18em] text-muted-foreground">{r.record_type === "legal_opinion" ? "Opinion" : "Litigation"}</span>
                            </div>
                            <div className="text-[10px] text-muted-foreground font-mono mt-0.5">
                                {r.original_suit_number && <span className="text-primary">{r.original_suit_number} · </span>}
                                {r.court_case_number || r.internal_number}
                                {r.client_name && <span> · {r.client_name}</span>}
                            </div>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}


/** Banner shown on every super-admin page when the demo tenant `demofirm`
 *  still exists in the database. On production this should always be empty;
 *  if it ever appears, the boot-time scrub failed and the user can click
 *  Scrub to clear it. Polls every 60s so a fresh leak surfaces quickly. */
function DemoDataLeakBanner() {
    const [present, setPresent] = useState(false);
    const [isProd, setIsProd] = useState(false);
    const [scrubbing, setScrubbing] = useState(false);

    const probe = useCallback(async () => {
        try {
            const { data } = await import("@/lib/api").then(m => m.api.get("/platform/demo-data-probe"));
            setPresent(Boolean(data?.demo_tenant_present || data?.demo_user_present));
            setIsProd(Boolean(data?.is_production_detected));
        } catch { /* silently ignore — non-super-admins get 403 */ }
    }, []);

    useEffect(() => {
        probe();
        const id = setInterval(probe, 60000);
        return () => clearInterval(id);
    }, [probe]);

    const scrub = async () => {
        setScrubbing(true);
        try {
            const api = (await import("@/lib/api")).api;
            const { data } = await api.post("/platform/scrub-demo-data");
            const { toast } = await import("sonner");
            toast.success(data.tenant_removed ? `Scrubbed ${data.rows_deleted} rows` : "All clear");
            probe();
        } catch (e) {
            const { toast } = await import("sonner");
            const { apiError } = await import("@/lib/api");
            toast.error(apiError(e));
        } finally { setScrubbing(false); }
    };

    if (!present) return null;
    return (
        <div className="mt-16 mx-4 sm:mx-6 lg:mx-8 border-l-4 border-amber-500 bg-amber-50 px-4 py-2.5 flex items-center gap-3 text-sm" data-testid="demo-data-leak-banner">
            <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0"/>
            <div className="flex-1 min-w-0">
                <span className="font-semibold text-amber-800">Demo data detected{isProd ? " on production" : ""}.</span>
                <span className="text-amber-700 ml-1">The <code className="bg-amber-100 px-1 rounded">demofirm</code> demo tenant exists in this database — clean it up to keep your tenant set tidy.</span>
            </div>
            <button onClick={scrub} disabled={scrubbing}
                data-testid="demo-leak-banner-scrub"
                className="inline-flex items-center gap-1.5 px-3 py-1 border border-amber-500 text-amber-700 hover:bg-amber-500 hover:text-white text-[10px] uppercase tracking-[0.16em]">
                {scrubbing ? "Scrubbing…" : "Scrub now"}
            </button>
        </div>
    );
}
